<script setup>
/**
 * The four numbers above the Policyholders tabs. Each one opens the tab it
 * counts, which is why the keys below are tab names rather than card names.
 */
import { computed } from 'vue';
import XlKpiRow from '../xl/XlKpiRow.vue';

const props = defineProps({
  accessGranted: { type: Number, default: 0 },
  notRegistered: { type: Number, default: 0 },
  registered: { type: Number, default: 0 },
  waitingForApproval: { type: Number, default: 0 },
  showRequests: { type: Boolean, default: true },
  // A broker filter narrows the other three cards but cannot narrow this one,
  // so the card has to say whose number it is showing.
  scopedToBroker: { type: Boolean, default: false },
  // The counts are on their way: each card holds its number's place.
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['select']);

const allCards = computed(() => [
  {
    // Both this and "Registered" list registered people, so both open that
    // tab. They used to share the key `registered` outright, which meant the
    // two cards were indistinguishable to anything downstream.
    key: 'access-granted',
    label: 'Access granted',
    value: props.accessGranted,
    tone: 'success',
    tooltip: 'Everyone registered, plus people added, imported or approved who haven\'t been sent an invitation yet. People invited by email count under Not registered until they register.',
  },
  {
    key: 'not-registered',
    label: 'Not registered',
    value: props.notRegistered,
    tone: 'brand',
    tooltip: 'People who can reach the portal but haven\'t registered yet. Some were invited; some were given access and never told.',
  },
  {
    key: 'registered',
    label: 'Registered',
    value: props.registered,
    tone: 'brand',
  },
  {
    key: 'requests',
    label: 'Waiting for approval',
    value: props.waitingForApproval,
    // Nought waiting is not an alarm, so it is only red while somebody is
    // actually being kept waiting.
    tone: props.waitingForApproval ? 'error' : '',
    hint: props.scopedToBroker
      ? 'across the agency'
      : (props.waitingForApproval ? 'needs a decision' : 'open the list'),
    tooltip: props.scopedToBroker
      ? 'People who asked to be let in. A request belongs to nobody yet, so this stays the whole agency\'s number even while you are viewing one broker.'
      : 'People who asked to be let in and are waiting on a decision.',
  },
]);

const cards = computed(() => allCards.value
  .filter((card) => props.showRequests || card.key !== 'requests')
  .map((card) => ({ ...card, loading: props.loading })));
</script>

<template>
  <XlKpiRow :cards="cards" @select="emit('select', $event)" />
</template>
